import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import InputLabel from '@material-ui/core/InputLabel'
import makeStyles from '@material-ui/core/styles/makeStyles'
import MenuItem from '@material-ui/core/MenuItem'
import React, { useState, useEffect } from 'react'
import Select from '@material-ui/core/Select'
import { useHistory } from 'react-router-dom'

import { Buttons } from '../../../../components/Steps'
import { Error, Subtitle, LoadingButton } from '../../../../components'
import { FormControl } from '../../../../components/Forms'
import { Main } from '../../../../components/Layout'
import { constants } from '../../../../utils'
import {
  appService,
  renewEnrollmentService,
  renewEnrollmentValidatorService,
} from '../../../../services'
import SubscriptionConflictDialog from '../../../../components/SubscriptionConflictDialog'
import RenewEnrollmentStatusModal from '../../../../components/RenewEnrollmentStatusModal'

const useStyles = makeStyles(() => ({
  alert: {
    marginBottom: '15px',
  },
  center: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  divider: {
    margin: '20px 0 25px',
  },
  error: { marginBottom: '16px' },
  main: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    width: '100%',
    padding: '30px',
  },
  formControl: {
    width: '100%',
    marginTop: '25px',
  },
}))

const Renewing = ({ data, setDone, setEnrollment, onBack, publicCall }) => {
  const classes = useStyles()
  const history = useHistory()

  const [curriculum, setCurriculum] = useState('')
  const [degree, setDegree] = useState('')
  const [degrees, setDegrees] = useState([])
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submiting, setSubmiting] = useState(null)
  const [noOfferMessage, setNoOfferMessage] = useState(null)
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false)
  const [subscriptionProtocol, setSubscriptionProtocol] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [jid, setJid] = useState(null)
  const shouldDisplayRequestButton = () => degrees.length > 0

  const shouldDisableRequestButton = () =>
    loading || submiting || !degree || !curriculum

  useEffect(() => {
    tryToGetNextOffers()
  }, [])

  useEffect(() => {
    setNoOfferMessage(
      appService.getLocalPublicCall().renewEnrollmentNoOfferMessage
    )
  }, [])

  const tryToGetNextOffers = async () => {
    setLoading(true)

    const { success, payload } = await renewEnrollmentService.nextOffers(
      data.id
    )

    if (success) {
      setDegrees(payload)
      setNotFound(payload.length < 1)

      if (payload.length === 1) {
        const record = payload[0]

        setDegree(record.id)

        try {
          if (record.curriculums.length === 1) {
            setCurriculum(record.curriculums[0].id)
          }
        } catch (e) {
          console.error(e)
        }
      }
    } else {
      setNotFound(true)
    }

    setLoading(false)
  }

  const performSubmit = async () => {
    const { success, error, jid } = await renewEnrollmentService.tryToSubmit(
      data.id,
      degree,
      curriculum
    )

    if (!success) {
      setError(error)
      setSubmiting(false)
      return
    }

    setJid(jid)
    setStatusModalOpen(true)
  }

  const tryToSubmit = async () => {
    setSubmiting(true)
    setError(null)

    const passedRenewEnrollmentCheck = await checkAlreadyHasRenewEnrollment()
    if (!passedRenewEnrollmentCheck) return

    const passedSubscriptionCheck = await ensureHasSubscriptionOrPrompt()
    if (!passedSubscriptionCheck) return

    await performSubmit()
    setSubmiting(false)
  }

  const checkAlreadyHasRenewEnrollment = async () => {
    try {
      await renewEnrollmentValidatorService.checkHasRenewEnrollment(data.id)
      return true
    } catch (err) {
      history.push(constants.routes.renewEnrollments.request.path, {
        error: err.message,
      })
      setSubmiting(false)
      return false
    }
  }

  const ensureHasSubscriptionOrPrompt = async () => {
    try {
      await renewEnrollmentValidatorService.checkHasSubscription(data.id)
      return true
    } catch (e) {
      setSubscriptionProtocol((e && e.subscriptionProtocol) || null)
      setSubscriptionDialogOpen(true)
      setSubmiting(false)
      return false
    }
  }

  const handleSubscriptionModalCancel = event => {
    if (
      event &&
      event.currentTarget &&
      typeof event.currentTarget.blur === 'function'
    ) {
      event.currentTarget.blur()
    }
    setSubscriptionDialogOpen(false)
  }

  const handleSubscriptionModalConfirm = async event => {
    if (
      event &&
      event.currentTarget &&
      typeof event.currentTarget.blur === 'function'
    ) {
      event.currentTarget.blur()
    }

    setSubscriptionDialogOpen(false)
    setSubmiting(true)
    setError(null)

    await performSubmit()
    setSubmiting(false)
  }

  const handleStatusModalClose = () => {
    setStatusModalOpen(false)
  }

  const handleStatusModalSuccess = (enrollmentData) => {
    setEnrollment(enrollmentData)
    setDone(true)
    setStatusModalOpen(false)
  }

  const handleStatusModalError = (errorMessage) => {
    setError(errorMessage)
    setStatusModalOpen(false)
  }

  const handleStatusModalRetry = () => {
    setStatusModalOpen(false)
    onBack()
  }

  const availableCurriculumsForDegree = () => {
    try {
      return degrees.find(i => i.id === degree).curriculums
    } catch {
      return []
    }
  }

  const renderError = message => (
    <Grid item xs={12} className={degrees.length > 0 ? classes.error : false}>
      <Error>
        <span>{message}</span>
      </Error>
    </Grid>
  )

  const renderBody = () => {
    if (notFound) {
      let fallback

      if (publicCall && publicCall.renewningText) {
        fallback = (
          <>
            {publicCall.renewningText}
          </>
        )
      } else {
        fallback = (
          <>
            Não foi possível encontrar a sua oferta nesta escola, consulte sua
            unidade escolar para ajuda. Caso a escola realmente não tenha oferta
            para rematricula no ano de escolaridade desejado,&nbsp;
            <a
              href={constants.routes.instructions.path}
              onClick={e => {
                e.preventDefault()
                history.push(constants.routes.instructions.path)
              }}
            >
              clique aqui
            </a>{' '}
            para solicitar a matrícula em uma outra escola da rede estadual.
          </>
        )
      }
      return renderError(noOfferMessage || fallback)
    } else {
      return (
        <>
          {!!error && renderError(error)}

          <Grid item xs={12}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="degree">
                Ano de escolaridade a ser cursado
              </InputLabel>
              <Select
                autoFocus={degrees.length > 1}
                disabled={loading || submiting}
                label="Ano de escolaridade a ser cursado"
                id="degree"
                value={degree}
                onChange={e => {
                  setCurriculum(null)
                  setDegree(e.target.value)
                }}
              >
                {degrees.map(i => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="curriculum">
                Organização curricular
              </InputLabel>
              <Select
                autoFocus={degrees.length === 1 && !curriculum}
                disabled={loading || submiting || !degree}
                label="Organização curricular"
                id="curriculum"
                value={curriculum}
                onChange={e => setCurriculum(e.target.value)}
              >
                {availableCurriculumsForDegree().map(i => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </>
      )
    }
  }

  const renderLoadingOrBody = () =>
    loading ? <CircularProgress size={20} /> : renderBody()

  return (
    <>
      <Main className={classes.main}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Subtitle>Detalhes da renovação da matrícula</Subtitle>
          </Grid>

          {renderLoadingOrBody()}
        </Grid>
      </Main>

      {shouldDisplayRequestButton() && (
        <Buttons extraClasses={classes.center}>
          <LoadingButton
            disabled={shouldDisableRequestButton()}
            loading={submiting}
            onClick={tryToSubmit}
          >
            Confirmar solicitação
          </LoadingButton>
        </Buttons>
      )}

      <SubscriptionConflictDialog
        open={subscriptionDialogOpen}
        protocol={subscriptionProtocol}
        onCancel={handleSubscriptionModalCancel}
        onConfirm={handleSubscriptionModalConfirm}
      />

      {statusModalOpen && (
        <RenewEnrollmentStatusModal
          jid={jid}
          groupStudentId={data.id}
          open={statusModalOpen}
          onClose={handleStatusModalClose}
          onSuccess={handleStatusModalSuccess}
          onError={handleStatusModalError}
          onRetry={handleStatusModalRetry}
        />
      )}
    </>
  )
}

export default Renewing
